import React, { useState, useEffect } from 'react';
import { Search, AlertCircle, DollarSign, CreditCard } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const DuePayment = () => {
  const [duePayments, setDuePayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetchDuePayments();
  }, []);

  const fetchDuePayments = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/consultants/reports/due-payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDuePayments(response.data);
    } catch (error) {
      console.error('Error fetching due payments:', error);
      alert('Failed to fetch due payments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filteredData = duePayments.filter(item =>
    item.consultant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const totalDue = filteredData.reduce((sum, item) => sum + item.balance_due, 0);
  const totalCommission = filteredData.reduce((sum, item) => sum + item.total_commission, 0);

  const handlePayNow = (consultantId) => {
    navigate('/consultants/payment', { state: { consultantId } });
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Due Payments</h1>
          <p className="text-sm text-gray-500 mt-1">Outstanding commission payments to consultants</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Due Amount</p>
              <p className="text-3xl font-bold text-red-600 mt-1">₹{totalDue.toLocaleString('en-IN')}</p>
            </div>
            <AlertCircle className="h-12 w-12 text-red-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total Commission</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">₹{totalCommission.toLocaleString('en-IN')}</p>
            </div>
            <DollarSign className="h-12 w-12 text-green-500" />
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Consultants with Due</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{filteredData.length}</p>
            </div>
            <CreditCard className="h-12 w-12 text-blue-500" />
          </div>
        </div>
      </div>

      {/* Search and Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-4 border-b border-gray-200">
          <div className="relative">
            <Search className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search consultants..."
              className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Consultant</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Students</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Commission</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Paid Amount</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Balance Due</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredData.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-6 py-8 text-center text-gray-500">No due payments found</td>
                </tr>
              ) : (
                filteredData.map((item) => (
                  <tr key={item.consultant_id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="text-sm font-medium text-gray-900">{item.consultant_name}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-500">{item.email}</div>
                      <div className="text-sm text-gray-500">{item.phone}</div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">{item.total_students}</td>
                    <td className="px-6 py-4 text-sm text-gray-900">₹{item.total_commission.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4 text-sm text-green-600">₹{item.total_paid.toLocaleString('en-IN')}</td>
                    <td className="px-6 py-4">
                      <span className="text-sm font-semibold text-red-600">₹{item.balance_due.toLocaleString('en-IN')}</span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <button
                        onClick={() => handlePayNow(item.consultant_id)}
                        className="text-blue-600 hover:text-blue-900 font-medium text-sm"
                      >
                        Pay Now
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default DuePayment;